import { Component, DestroyRef, Inject, inject } from '@angular/core';
import { StateService, Transition, TransitionService } from '@uirouter/core';
import {
  compareIgnoringFields,
  deepClone,
} from 'src/app/shared/helpers/object.helper';
import { ModalService } from '../../../components/modals/modal.service';

@Component({
  template: '',
  standalone: false,
})
export abstract class BaseEditableComponent<T> {
  private bypassUnsaved = false;
  protected stateService = inject(StateService);
  protected transitionService = inject(TransitionService);
  protected modalService = inject(ModalService);
  protected destroyRef = inject(DestroyRef, { optional: true });
  private transitionHook: Function;
  private _oldObject: T;
  private _transition: Transition;
  protected fieldsToIgnore: string[] = [];

  constructor(@Inject(String) currentStateName?: string) {
    this.transitionHook = this.transitionService.onStart(
      currentStateName
        ? { to: (state: any) => state.name.indexOf(currentStateName) === -1 }
        : {},
      (trans: Transition) => {
        if (trans.options().source === 'url') {
          if (!this.canDeactivate() && !this.bypassUnsaved) {
            this._transition = trans;
            trans.abort();
            return;
          }
          return;
        }
        if (trans.to().name === trans.from().name) return;
        if (!this.canDeactivate() && !this.bypassUnsaved) {
          trans.abort();
          this.handleUnsavedChanges(trans);
        }
      },
    );
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    window.addEventListener('popstate', this.popstateHandler);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      window.removeEventListener('popstate', this.popstateHandler);
      this.transitionHook();
    });
  }

  popstateHandler = () => {
    if (!this.canDeactivate() && !this.bypassUnsaved && this._transition) {
      this.handleUnsavedChanges(this._transition).finally(() => {
        this._transition = null;
      });
    } else {
      this.stateService.go(this._transition.to().name);
      this._transition = null;
    }
  };

  beforeUnloadHandler = (event: Event) => {
    if (!this.canDeactivate() && !this.bypassUnsaved) {
      event.preventDefault();
      (event as BeforeUnloadEvent).returnValue = true;
      return 'You have unsaved changes. Do you want to Discard them?';
    }
  };

  private handleUnsavedChanges(trans: Transition) {
    return this.modalService
      .confirmDanger(
        'Unsaved Changes',
        'You have unsaved changes. Do you want to Discard them?',
        'Discard',
      )
      .then(() => {
        this.bypassUnsaved = true;
        this.stateService.go(trans.to().name);
      })
      .catch(() => {});
  }

  abstract canDeactivate(): boolean;

  protected isDeepEqual<T>(obj1: T, obj2: T): boolean {
    return compareIgnoringFields(obj1, obj2, this.fieldsToIgnore);
  }

  protected cloneDeep<T>(obj: T): T {
    return deepClone(obj);
  }

  bypassUnsavedChanges() {
    this.bypassUnsaved = true;
  }

  protected resetUnsavedChanges() {
    this.bypassUnsaved = false;
  }

  protected set oldObject(obj: T) {
    this._oldObject = obj;
  }

  protected get oldObject(): T {
    return this._oldObject;
  }

  protected hasInitialObject(): boolean {
    return this._oldObject !== undefined;
  }
}
